import React, { useEffect, useState } from "react";
import { Table, Tag, Spin } from "antd";
import {
  IconCreditCard,
  IconBuildingBank,
  IconCategory,
  IconNews,
  IconSpeakerphone,
  IconReportMoney,
  IconPigMoney,
  IconShieldCheck,
  IconHome,
  IconAlertTriangle,
} from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import api from "../../api";
import { useToast } from "../../components/toast";

const PRODUCT_ICONS = {
  card: { icon: <IconCreditCard size={20} />, color: "#1677ff", bg: "#e6f4ff" },
  loan: {
    icon: <IconReportMoney size={20} />,
    color: "#eb2f96",
    bg: "#fff0f6",
  },
  savings: {
    icon: <IconPigMoney size={20} />,
    color: "#52c41a",
    bg: "#f6ffed",
  },
  insurance: {
    icon: <IconShieldCheck size={20} />,
    color: "#722ed1",
    bg: "#f9f0ff",
  },
  apartment: { icon: <IconHome size={20} />, color: "#fa8c16", bg: "#fff7e6" },
};

// Дуусахад хэд хоног үлдсэн
const daysLeft = (endsAt) => {
  const diff = Math.ceil(
    (new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24),
  );
  return diff <= 0 ? "өнөөдөр" : `${diff} хоног`;
};

const expiringPromoColumns = [
  {
    title: "Бүтээгдэхүүн",
    dataIndex: "productName",
    render: (v, r) => (
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
        <div style={{ fontSize: 11, color: "#888" }}>{r.typeLabel}</div>
      </div>
    ),
  },
  {
    title: "Дуусах",
    dataIndex: "endsAt",
    width: 120,
    render: (v) => {
      const left = daysLeft(v);
      return (
        <Tag color={left === "өнөөдөр" ? "red" : "orange"}>
          {left === "өнөөдөр" ? "Өнөөдөр дуусна" : `${left} үлдсэн`}
        </Tag>
      );
    },
  },
];

const recentProductColumns = [
  {
    title: "Бүтээгдэхүүн",
    dataIndex: "name",
    render: (v, r) => (
      <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
        {r.imageUrl && (
          <img
            src={r.imageUrl}
            alt={v}
            style={{ width: 40, height: 25, objectFit: "contain" }}
          />
        )}
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
          <div style={{ fontSize: 11, color: "#888" }}>{r.issuer?.name}</div>
        </div>
      </div>
    ),
  },
  {
    title: "Төрөл",
    dataIndex: "productType",
    width: 200,
    render: (v) => <Tag>{v}</Tag>,
  },
  {
    title: "Нэмсэн",
    dataIndex: "createdAt",
    width: 200,
    render: (v) => new Date(v).toLocaleDateString("mn-MN"),
  },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchDash = async () => {
      setLoading(true);
      const res = await api.dash();
      if (res?.status === 200) {
        setData(res.data);
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
      setLoading(false);
    };
    fetchDash();
  }, []);

  if (loading) {
    return (
      <section className="dashboard-section">
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="small" />
        </div>
      </section>
    );
  }

  const { products, stats, attention, recentProducts } = data;

  const SYSTEM_STATS = [
    {
      key: "issuers",
      label: "Байгууллага",
      value: stats.totalIssuers,
      icon: <IconBuildingBank size={20} />,
      color: "#722ed1",
      bg: "#f9f0ff",
    },
    {
      key: "categories",
      label: "Ангилал",
      value: stats.totalCategories,
      icon: <IconCategory size={20} />,
      color: "#13c2c2",
      bg: "#e6fffb",
    },
    {
      key: "news",
      label: "Мэдээ",
      value: stats.totalNews,
      icon: <IconNews size={20} />,
      color: "#fa8c16",
      bg: "#fff7e6",
      sub: `${stats.publishedNews} нийтлэгдсэн · ${stats.draftNews} ноорог`,
    },
    {
      key: "promotions",
      label: "Промо",
      value: stats.totalPromotions,
      icon: <IconSpeakerphone size={20} />,
      color: "#52c41a",
      bg: "#f6ffed",
      sub: `${stats.activePromotions} идэвхтэй`,
    },
  ];

  return (
    <section className="dashboard-section">
      <PageHeader
        title="Удирдлагын хяналтын самбар"
        sub="Системийн ерөнхий байдал ба гол үзүүлэлтүүд"
      />

      {/* ── Системийн stat — grid-2 ── */}
      <div className="dash-side-grid">
        {SYSTEM_STATS.map((s) => (
          <div key={s.key} className="dash-stat-card">
            <div
              className="dash-stat-icon"
              style={{ background: s.bg, color: s.color }}
            >
              {s.icon}
            </div>
            <div className="dash-stat-body">
              <div className="dash-stat-label">{s.label}</div>
              {s.sub && <div className="dash-stat-sub">{s.sub}</div>}
            </div>
            <div className="dash-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="dash-stats">
        {products.map((p) => {
          const ic = PRODUCT_ICONS[p.key];
          return (
            <div key={p.key} className="dash-stat-card">
              <div
                className="dash-stat-icon"
                style={{ background: ic.bg, color: ic.color }}
              >
                {ic.icon}
              </div>
              <div className="dash-stat-body">
                <div className="dash-stat-label">{p.label}</div>
                <div className="dash-stat-sub">{p.featured} онцолсон</div>
              </div>
              <div className="dash-stat-value">{p.total}</div>
            </div>
          );
        })}
      </div>

      <div className="dash-layout">
        {/* ══ Зүүн багана — 2/3 ══ */}
        <div className="dash-layout-main">
          {/* ── Бүтээгдэхүүний тойм (5 төрөл) ── */}

          {/* ── Сүүлд нэмэгдсэн бүтээгдэхүүн ── */}
          <div className="dash-table-block">
            <div className="dash-section-title">
              <IconCreditCard size={16} />
              Сүүлд нэмэгдсэн бүтээгдэхүүн
            </div>
            <Table
              rowKey="id"
              size="small"
              columns={recentProductColumns}
              dataSource={recentProducts}
              pagination={false}
            />
          </div>
        </div>

        {/* ══ Баруун багана — 1/3 ══ */}
        <div className="dash-layout-side">
          {/* ── Дуусаж буй промо ── */}
          {attention?.expiringPromotions?.length > 0 && (
            <div className="dash-table-block">
              <div className="dash-section-title dash-section-title--warning">
                <IconAlertTriangle size={16} />
                Дуусаж буй промо
              </div>
              <Table
                rowKey="id"
                size="small"
                columns={expiringPromoColumns}
                dataSource={attention.expiringPromotions}
                pagination={false}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
